import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, Clock, User, Shield, Database } from 'lucide-react';
import { adminService } from '../services/adminService';
import toast from 'react-hot-toast';

const AuditLogs = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('all');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getAuditLogs();
      
      if (Array.isArray(data)) {
        setLogs(data);
        setError(null);
      } else {
        setLogs([]);
        setError('Invalid data format received from server');
      }
    } catch (err) {
      setLogs([]);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const mockLogs = [
    {
      id: 1,
      user_name: 'Admin User',
      action: 'user_login',
      description: 'User logged in successfully',
      ip_address: '192.168.1.100',
      user_agent: 'Mozilla/5.0...',
      created_at: '2024-01-20T10:00:00.000Z',
      severity: 'info',
    },
    {
      id: 2,
      user_name: 'John Doe',
      action: 'user_created',
      description: 'New user account created',
      ip_address: '192.168.1.101',
      user_agent: 'Mozilla/5.0...',
      created_at: '2024-01-19T15:30:00.000Z',
      severity: 'info',
    },
  ];

  const displayLogs = logs.length > 0 ? logs : mockLogs;
  
  const filteredLogs = displayLogs.filter(log => {
    const matchesSearch = (
      (log.user_name && log.user_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (log.action && log.action.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (log.description && log.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const matchesAction = selectedAction === 'all' || log.action === selectedAction;
    return matchesSearch && matchesAction;
  });

  const getSeverityBadge = (severity) => {
    const config = {
      info: 'bg-blue-100 text-blue-800',
      warning: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      critical: 'bg-purple-100 text-purple-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${config[severity] || config.info}`}>
        {severity || 'info'}
      </span>
    );
  };

  const getActionIcon = (action) => {
    if (action.includes('login')) return <User className="h-4 w-4" />;
    if (action.includes('user')) return <User className="h-4 w-4" />;
    if (action.includes('security')) return <Shield className="h-4 w-4" />;
    return <Database className="h-4 w-4" />;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600">Monitor system activities and user actions</p>
        </div>
        <button className="btn-secondary flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export Logs
        </button>
      </div>

      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              className="input-field"
            >
              <option value="all">All Actions</option>
              <option value="user_login">User Login</option>
              <option value="user_created">User Created</option>
              <option value="user_updated">User Updated</option>
              <option value="user_deleted">User Deleted</option>
            </select>
            <button className="btn-secondary flex items-center gap-2">
              <Filter className="h-4 w-4" />
              More Filters
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading audit logs...</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredLogs.map((log) => (
            <div key={log.id} className="card p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 rounded-lg bg-gray-100">
                      {getActionIcon(log.action)}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{log.user_name}</h3>
                    {getSeverityBadge(log.severity)}
                  </div>
                  <p className="text-gray-600 mb-3">{log.description}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {new Date(log.created_at).toLocaleDateString()}
                    </div>
                    <span>IP: {log.ip_address}</span>
                    <span>Action: {log.action}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredLogs.length === 0 && (
        <div className="card p-12 text-center">
          <div className="mx-auto h-12 w-12 text-gray-400 mb-4">
            <Database className="h-12 w-12" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No audit logs found</h3>
          <p className="text-gray-500">No activity has been logged yet.</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
